import React from 'react';

import { CSVLink } from 'react-csv';
import { Link } from 'react-router-dom';

import flatMap from 'lodash/flatMap';
import floor from 'lodash/floor';
import get from 'lodash/get';
import maxBy from 'lodash/maxBy';
import reduce from 'lodash/reduce';
import sortBy from 'lodash/sortBy';
import sortedUniqBy from 'lodash/sortedUniqBy';
import times from 'lodash/times';

import { chartYAxisFormatter, scientificNotationFormatter } from '../formatters';
import { getHostnameWithWorkspace, preserveWorkspaceURL } from '../url_utils';

import ClipboardLabel from '../../components/common/ClipboardLabel/ClipboardLabel';
import Label from '../../components/common/Labels/Label';

export const isEmptyChartValue = value => (value === null || value === undefined || value === 'NaN');
export const isZeroChartValue = value => (value === 0 || value === '0');
export const isZeroOrEmptyChartValue = value => isZeroChartValue(value) || isEmptyChartValue(value);

export const filterInvalidChartValues = value => isZeroOrEmptyChartValue(value) ? null : value;

export const getMaxChartValue = (chartData = []) => maxBy(chartData, point => parseFloat(point)) || 0;
export const getMaxChartValueAcrossDatasets = (chartDatasets = []) => getMaxChartValue(maxBy(chartDatasets, dataset => getMaxChartValue(dataset.data))?.data) || 0

export const hasNoData = (chartData = []) => reduce(chartData, (output, curr) => {
  if (!output) {
    return false;
  }
  return isZeroOrEmptyChartValue(curr);
}, true);

export const datasetsHaveNoData = (datasets = []) => {
  if (!datasets || datasets.length === 0) {
    return true;
  }
  return reduce(datasets, (output, dataset) => {
    if (!output) {
      return false;
    }
    return hasNoData(dataset.data);
  }, true)
}

export const filterExcessZeroes = (chartData = []) => reduce(chartData, (output, curr, index) => {
  if (isZeroOrEmptyChartValue(curr[1])) {
    const prevValue = get(chartData, `[${index - 1}][1]`, 0);
    const nextValue = get(chartData, `[${index + 1}][1]`, 0);

    if (isZeroOrEmptyChartValue(prevValue) && isZeroOrEmptyChartValue(nextValue)) {
      return [...output, [curr[0], null]];
    }
    return [...output, [curr[0], '0']];
  }

  return [...output, curr];
}, []);

export const getNearestValueBefore = (timestamp, chartData = []) => reduce(chartData, (currentMin, currentValue) => {
  if (currentValue[0] > timestamp) {
    return currentMin;
  }
  if (currentMin === null || timestamp === currentValue[0]) {
    return currentValue;
  }
  if (Math.abs(timestamp - currentValue[0]) < Math.abs(timestamp - currentMin[0])) {
    return currentValue;
  }
  return currentMin;
}, null)

const getNumberOfTicksForRange = (startTimestamp, endTimestamp, tickSize) => floor((endTimestamp - startTimestamp) / tickSize) + 1

export const generateChartLabelsForTimeRange = (startTimestamp, endTimestamp, tickSize) => {
  const numberOfTicks = getNumberOfTicksForRange(startTimestamp, endTimestamp, tickSize)
  return times(numberOfTicks).map(index => startTimestamp + (tickSize * index));
}

export const fillInMissingPoints = (chartData, startTimestamp, endTimestamp) => {
  if (!chartData?.length) {
    return [];
  }
  if (chartData.length < 2) {
    return chartData;
  }

  const firstTick = chartData[0][0];
  const secondTick = chartData[1][0];
  const tickSize = secondTick - firstTick;
  const numberOfTicks = getNumberOfTicksForRange(startTimestamp, endTimestamp, tickSize)

  return times(numberOfTicks).map((index) => {
    const timestamp = startTimestamp + (tickSize * index)
    const nearestValue = getNearestValueBefore(timestamp, chartData);
    const actualNearestValue = get(nearestValue, '[1]', null);

    return [timestamp, actualNearestValue];
  });
}

export const removeDuplicateChartEntries = (values) => {
  const byTimestamp = entry => entry[0];
  return sortedUniqBy(sortBy(values, byTimestamp), byTimestamp)
}

export const transformResultsIntoValues = resultSets => removeDuplicateChartEntries(flatMap(resultSets, resultSet => resultSet?.values || []))

const generateCsvTitle = (filePrefix, title) => {
  const currentDate = new Date();
  const offset = currentDate.getTimezoneOffset();

  const dateWithOffset = new Date(currentDate.getTime() - offset * 60 * 1000);
  const yearMonthDay = dateWithOffset.toISOString().split('T')[0];

  return `${filePrefix}_${title.toLowerCase().replaceAll(' ', '-')}_${yearMonthDay}.csv`;
};

export const getPipelineChartLink = ({ chartID, pipelineID, pipelineVersion }) => `/pipelines/${pipelineID}/versions/${pipelineVersion}/chart/${chartID}`

export const getDefaultChartDropdownOptions = ({ 
  csvData, csvFilenamePrefix, csvFileTitle, shareableChartLink 
}) => [
  {
    id: 'downloadCSV',
    label: 'Download CSV',
    render: ({ id, label, onValueSelected }) => (
      <CSVLink
        data={csvData}
        filename={generateCsvTitle(csvFilenamePrefix, csvFileTitle)}
        onClick={() => onValueSelected(id)}
        className="text-left text-brand py-1 px-4"
      >
        {label}
      </CSVLink>
    ),
  },
  {
    id: 'shareChart',
    label: 'Copy shareable URL',
    render: ({ id, label, onValueSelected }) => (
      <ClipboardLabel
        className="text-left py-1 px-4"
        labelSize="md"
        labelValue={`${getHostnameWithWorkspace(true)}${shareableChartLink}`}
        displayLabel={label}
        onCopyResult={() => {
          onValueSelected(id)
        }}
      >
        <Label>{label}</Label>
      </ClipboardLabel>
    ),
  },
]

export const getPipelineChartDropdownOption = ({
  pipelineID, pipelineVersion, shareableChartLink, isEnlarged = false,
}) => isEnlarged ? {
  id: 'goToPipeline',
  label: 'Go To Pipeline',
  render: ({ label }) => (
    <Link to={preserveWorkspaceURL(`/pipelines/${pipelineID}/versions/${pipelineVersion}`)}>
      <Label className="text-left py-1 px-4">
        {label}
      </Label>
    </Link>
  )
} : {
  id: 'goToLargeChart',
  label: 'View Enlarged',
  render: ({ label }) => (
    <Link to={`/${getHostnameWithWorkspace(false)}${shareableChartLink}`}>
      <Label className="text-left py-1 px-4">
        {label}
      </Label>
    </Link>
  )
}

export const getChartDropdownOptions = ({
  chartLink,
  chartTitle,
  csvData,
  pipelineID,
  pipelineVersion,
  isEnlarged = false,
}) => {
  const defaultDropdownOptions = getDefaultChartDropdownOptions({
    csvData,
    csvFilenamePrefix: pipelineID,
    csvFileTitle: chartTitle,
    shareableChartLink: chartLink,
  })

  const pipelineDropdownOption = getPipelineChartDropdownOption({
    pipelineID, pipelineVersion, shareableChartLink: chartLink, isEnlarged,
  })

  return [...defaultDropdownOptions, pipelineDropdownOption]
}

export const fallbackToScientificNotation = (val) => {
  if (val >= 10000) {
    return scientificNotationFormatter.format(val);
  }
  return chartYAxisFormatter.format(val);
}
